import React, { useState } from 'react'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import {
  compose, color, space, typography, SpaceProps
} from 'styled-system'
import { useDidMountEffect } from '@zuto/utilities'
import { Input, ErrorMessage } from '@zuto/atoms'
import { LabelVariants } from '@zuto/atoms/src/Form/FormLabel'

export interface DateOfBirthValues {
  day?: string,
  month?: string,
  year?: string
}
export interface DateOfBirthProps extends
  Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue'>, SpaceProps {
  error?: string
  defaultValue?: DateOfBirthValues
  valueChanged?: (_: DateOfBirthValues) => void
  labelVariant?: LabelVariants
  autoFocus?: boolean
}

const Wrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  ${space}
`
const StyledDateOfBirthSeperator = styled.div`
  ${themeGet('dateOfBirthSeperator')}
  ${compose(space, color, typography)}
`

const SmallInput = styled(Input)`
  flex-basis: 23%
`

const LargeInput = styled(Input)`
  flex-basis: 30%
`

const StyledErrorMessage = styled(ErrorMessage)`
  flex-basis: 100%;
`

const DateOfBirth = ({
  error, defaultValue, valueChanged, onBlur, labelVariant, autoFocus = false, ...props
}: DateOfBirthProps): JSX.Element => {
  const [values, setValues] = useState<DateOfBirthValues>(defaultValue || {})

  useDidMountEffect(() => {
    if (valueChanged) {
      valueChanged(values)
    }
  }, [values])

  const inputRefs: (HTMLInputElement | null)[] = []

  const sliceToMaxLength = (value: string, maxLength: string | null) => {
    if (!maxLength) {
      return value
    }

    return value.slice(0, parseInt(maxLength, 10))
  }

  const eventHandler = (input: HTMLInputElement): string => {
    let newValue = input.value.replace(/[^0-9]/g, '')
    newValue = sliceToMaxLength(newValue, input.getAttribute('maxLength'))

    return newValue
  }

  const autoFocusNext = (input: HTMLInputElement) => {
    const maxLength = input.getAttribute('maxlength')
    if (maxLength && input.value.length >= parseInt(maxLength, 10)) {
      const currentInputIndex = inputRefs.indexOf(input)
      if (currentInputIndex === inputRefs.length - 1) {
        return
      }
      const nextInput = inputRefs[currentInputIndex + 1]

      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.value = eventHandler(e.currentTarget)
    const { name } = e.currentTarget
    const { value } = e.currentTarget
    setValues((prev) => ({
      ...prev,
      [name]: value
    }))
    autoFocusNext(e.currentTarget)
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.value = eventHandler(e.currentTarget)
    if (onBlur) onBlur(e)
  }

  return (
    <Wrapper {...props} data-hj-suppress="">
      <SmallInput
        autoFocus={autoFocus}
        labelVariant={labelVariant}
        data-testid="day-input"
        ref={(r) => inputRefs.push(r)}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        type="number"
        min="1"
        max="31"
        maxLength={2}
        name="day"
        label="Day"
        addErrorClass={!!error}
        defaultValue={defaultValue?.day}
        placeholder="DD"
        pattern="[0-9]*"
        data-hj-suppress=""
      />
      <StyledDateOfBirthSeperator>/</StyledDateOfBirthSeperator>
      <SmallInput
        labelVariant={labelVariant}
        data-testid="month-input"
        ref={(r) => inputRefs.push(r)}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        type="number"
        min="1"
        max="12"
        maxLength={2}
        name="month"
        label="Month"
        addErrorClass={!!error}
        defaultValue={defaultValue?.month}
        placeholder="MM"
        pattern="[0-9]*"
        data-hj-suppress=""
      />
      <StyledDateOfBirthSeperator>/</StyledDateOfBirthSeperator>
      <LargeInput
        labelVariant={labelVariant}
        data-testid="year-input"
        ref={(r) => inputRefs.push(r)}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        type="number"
        min="1900"
        maxLength={4}
        name="year"
        label="Year"
        addErrorClass={!!error}
        defaultValue={defaultValue?.year}
        placeholder="YYYY"
        pattern="[0-9]*"
        data-hj-suppress=""
      />
      {error && (
        <StyledErrorMessage>
          {error}
        </StyledErrorMessage>
      )}
    </Wrapper>
  )
}

export default DateOfBirth
